import React from 'react';
import PropTypes from 'prop-types';
import store from '@Store';
import isUndefined from 'lodash/isUndefined';
import pick from 'lodash/pick';
import filter from 'lodash/filter';
import some from 'lodash/some';
import fromPairs from 'lodash/fromPairs';
import find from 'lodash/find';
import get from 'lodash/get';
import FactsContext from '@Components/Results/substances/contexts/facts.context';
import { SearchTypes } from '@Constants/CommonConstants';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import StructureModel from '@Components/Results/structure/structure-model.service';
import StructureZoomService from '@Services/StructureZoom';
import SubstanceIdentificationInformation from '@Components/Results/substances/information/substance-identification-information.component';
import SubstanceMoreNamesInformation from '@Components/Results/substances/information/substance-more-names-information.component';
import SubstanceSupplierInformation from '@Components/Results/substances/information/substance-supplier-information.component';
import Structure from '@Components/Results/structure/Structure';

import SubstanceHelper from '@Components/Results/services/substance-helper.factory';
import SubstanceDetailsContainer from '@Components/Results/substances/details/substance-details-container.component';
import SubstanceHitData from '@Components/Results/substances/information/substance-hit-data.component';
import SubstanceInformation from '@Components/Results/substances/information/substance-information.component';
import { contextFromMetadata } from '@Components/Results/utils/metadata';
import { getExpandTopSubstanceFactMappingValue, getUIDirectiveValue, UI_DIRECTIVES } from '@Utils/UIDirectivesParameterHelper';
import Catalyst from '@Services/Catalyst';

// RX-3549 - remove imports once feature switcher is removed

import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';

function prepareFacts(facts, isMarkushSearch) {
  return (
    facts &&
    fromPairs(
      facts.map((fact) => {
        if (isMarkushSearch && fact.id === 'ID') {
          const markushFact = find(fact.children, { type: 'markush' });
          if (markushFact) {
            markushFact.open = true;
          }
        }

        return [
          fact.id,
          {
            ...fact,
            open: fact.hitData || (isMarkushSearch && fact.id === 'ID'),
          },
        ];
      })
    )
  );
}

export default class SubstanceComponent extends React.PureComponent {
  constructor(props) {
    super(props);

    const { metadata, substance, searchType } = props;
    const isMarkushSearch = searchType === SearchTypes.MARKUSH_SEARCH;
    const facts = prepareFacts(substance.facts, isMarkushSearch);

    this.state = {
      isFactsDataOpen: isMarkushSearch,
      alternativeContent: {},
      facts,
    };

    this.zoomClasses = StructureZoomService.getZoomClasses('substances') || [];
    this.database = SubstanceHelper.getCurrentDatabase(metadata.databaseId || substance.databaseId);
    this.substance = new StructureModel(substance);

    this.toggleFact = this.toggleFact.bind(this);
  }

  componentDidMount() {
    this.autoOpenCategory();
  }

  // eslint-disable-next-line consistent-return
  onToggle = (id, header) => {
    const { isReaxysContent, isCIDatabase, isCommercialSupplierData } = this.database;
    const { facts } = this.state;
    const fact = get(facts, id);

    if (get(fact, 'hitData')) {
      return this.toggleFact(fact.id, true, true);
    }

    if (isReaxysContent || isCIDatabase || isCommercialSupplierData) {
      return this.toggleState('isFactsDataOpen', !fact.open, fact, header);
    }

    this.onTogleAlternativeInfo(id);
  };

  onTogleAlternativeInfo = (id) => {
    const { alternativeContent } = this.state;

    alternativeContent[id] = !alternativeContent[id];

    this.setState({
      alternativeContent: { ...alternativeContent },
    });
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  autoOpenCategory = () => {
    const { metadata, size, substance } = this.props;
    const { isCommercialSupplierData } = this.database;
    const { facts } = this.state;

    if (Auth.isFeatureEnabled(FEATURES.enableDisplaySupplierNumber) && isCommercialSupplierData && substance.index === '1') {
      const proddFact = get(facts, 'PRODD');
      const hitDataFact = get(facts, 'HIT_DATA');
      if (proddFact && !hitDataFact) {
        this.onToggle('PRODD');
      }
      return;
    }
    if (metadata.heatmapMode && size === 1) {
      filter(facts, (fact) => fact.type === 'category').forEach((fact) => this.onToggle(fact.id));
    }

    // hop in to expand fact directive
    const expandTopSubstanceFactDirectiveValue =
      substance.index === '1' &&
      metadata.uiDirectives &&
      getUIDirectiveValue(metadata.uiDirectives, UI_DIRECTIVES.EXPAND_TOP_SUBSTANCE_FACT);
    const expandTopSubstanceFactDirectiveAltValue = getExpandTopSubstanceFactMappingValue(expandTopSubstanceFactDirectiveValue);
    if (expandTopSubstanceFactDirectiveValue && some(facts, { id: expandTopSubstanceFactDirectiveValue })) {
      this.onToggle(expandTopSubstanceFactDirectiveValue);
    } else if (expandTopSubstanceFactDirectiveAltValue && some(facts, { id: expandTopSubstanceFactDirectiveAltValue })) {
      // if fact not found directly try mapping alias
      this.onToggle(expandTopSubstanceFactDirectiveAltValue);
    }
  };

  // eslint-disable-next-line react/sort-comp
  toggleFact(id, open, shouldScroll) {
    // eslint-disable-next-line react/destructuring-assignment
    if (!this.state.facts) return;

    const { metadata } = this.props;
    // eslint-disable-next-line react/destructuring-assignment
    const facts = { ...this.state.facts };
    const originalState = facts[id].open;
    facts[id] = {
      ...facts[id],
      open: isUndefined(open) ? !originalState : open,
    };

    const currentFact = facts[id];
    if (currentFact) {
      // eslint-disable-next-line consistent-return
      return SubstanceHelper.checkAndRetrieveFactData(currentFact, this.substance, metadata)
        .then(() => {
          const isOpen = currentFact.open;
          if (originalState !== isOpen) {
            this.setState({ facts });
          }
          if (isOpen) {
            Catalyst.trackAmountOfViewedData(
              get(this, 'substance.details.id', ''),
              currentFact,
              currentFact.type,
              currentFact.title,
              store.getState()
            );
          }
          if (shouldScroll) {
            const targetSelector = `#substances-${currentFact.hitData ? 'hitdata' : 'alldata'}-${this.substance.index} .rx-accordion-header.${
              currentFact.className
            }`;
            const target = document.querySelector(targetSelector);
            if (target) {
              target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
          return isOpen;
        })
        .catch(Logger.reject);
    }
    const errorMessage = `Can't find fact by the ID, the data is corrupted.`;
    Catalyst.trackError(
      {
        message: errorMessage,
        component: 'Substance',
      },
      'se-'
    );
    // new Error(errorMessage);
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  toggleState(flag, value, fact, header) {
    const { metadata } = this.props;
    const { isFactsDataOpen } = this.state;
    const state = pick(this.state, flag);
    const originalState = state[flag];

    // if we click substance name and substance panel is opened -> close substance panel
    if (header && originalState) {
      state[flag] = false;
      // if we click some fact -> open substance panel
    } else if (fact && fact.id) {
      state[flag] = true;
    } else {
      state[flag] = !isUndefined(value) ? value : !originalState;
    }

    if (originalState !== state[flag]) {
      this.setState(state);
    }

    if (fact) {
      if (!fact.open || !isFactsDataOpen) {
        if (metadata.isViewPreview) {
          Catalyst.trackContentLinkClick(fact.title, `query:preview:${contextFromMetadata(metadata)}`);
        } else {
          Catalyst.trackContentLinkClick(fact.title);
        }
      }

      const id = `accordion-${fact.className}-${this.substance.index}`;

      // do not toggle opened fact inside closed wrapper
      if (!originalState && fact.open) {
        setTimeout(() => {
          const target = document.getElementById(id);
          if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      } else {
        this.toggleFact(fact.id, value)
          .then((open) => open)
          .catch(Logger.error);
      }
    }
  }

  toggleSuppliers = () => {
    this.onTogleAlternativeInfo('supplierInformation');
  };

  toggleCommercialSuppliers = () => {
    this.onTogleAlternativeInfo('commercialSupplierInformation');
  };

  toggleIdentification = () => {
    this.onTogleAlternativeInfo('identification');
  };

  toggleMoreNames = () => {
    this.onTogleAlternativeInfo('moreNames');
  };

  render() {
    const { metadata, gridView } = this.props;
    const { isFactsDataOpen, alternativeContent, facts } = this.state;
    const { identification, supplierInformation, moreNames } = alternativeContent;
    // eslint-disable-next-line react/destructuring-assignment
    const zoom = metadata.isViewPreview ? 0 : this.props.zoom;
    const zoomClass = this.zoomClasses[zoom];

    return (
      <div className="rx-element-content">
        {/* eslint-disable-next-line react/jsx-no-constructed-context-values */}
        <FactsContext.Provider value={{ facts, toggle: this.toggleFact }}>
          <div className="rx-element--substance__wrapper">
            <div className={`substances-item-wrapper ${zoomClass}`}>
              <div className="substance-item" draggable="false">
                <Structure metadata={metadata} zoom={zoom} structure={this.substance} />
              </div>
            </div>

            {!gridView && (
              <SubstanceDetailsContainer
                metadata={metadata}
                substance={this.substance}
                database={this.database}
                onToggle={this.onToggle}
                toggleSuppliers={this.toggleSuppliers}
                toggleCommercialSuppliers={this.toggleCommercialSuppliers}
                toggleIdentification={this.toggleIdentification}
                toggleMoreNames={this.toggleMoreNames}
              />
            )}
          </div>

          <SubstanceHitData metadata={metadata} substance={this.substance} database={this.database} />

          {isFactsDataOpen && (
            <SubstanceInformation
              metadata={metadata}
              facts={facts}
              toggleFact={this.toggleFact}
              substance={this.substance}
              database={this.database}
              onToggle={() => this.toggleState('isFactsDataOpen', false)}
              zoom={zoom}
            />
          )}

          {identification && (
            <SubstanceIdentificationInformation substance={this.substance} database={this.database} onToggle={this.toggleIdentification} />
          )}

          {supplierInformation && (
            <SubstanceSupplierInformation substance={this.substance} database={this.database} onToggle={this.toggleSuppliers} />
          )}

          {moreNames && <SubstanceMoreNamesInformation substance={this.substance} onToggle={this.toggleMoreNames} />}
        </FactsContext.Provider>
      </div>
    );
  }
}

SubstanceComponent.propTypes = {
  metadata: MetadataPropTypesShape,
  substance: PropTypes.object.isRequired,
  zoom: PropTypes.number.isRequired,
  searchType: PropTypes.string,
  gridView: PropTypes.bool.isRequired,
  size: PropTypes.number,
};
